import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Download, ChevronRight, Search, ArrowUpRight, Filter, Globe, Shield, Lock, Gift, CreditCard, FileText, Presentation, RefreshCw, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WORLD_COUNTRIES = [
  "Afghanistan","Albania","Algeria","Andorra","Angola","Antigua and Barbuda","Argentina","Armenia","Australia","Austria",
  "Azerbaijan","Bahamas","Bahrain","Bangladesh","Barbados","Belarus","Belgium","Belize","Benin","Bhutan",
  "Bolivia","Bosnia and Herzegovina","Botswana","Brazil","Brunei","Bulgaria","Burkina Faso","Burundi","Cabo Verde","Cambodia",
  "Cameroon","Canada","Central African Republic","Chad","Chile","China","Colombia","Comoros","Congo (DRC)","Congo (Republic)",
  "Costa Rica","Cote d'Ivoire","Croatia","Cuba","Cyprus","Czech Republic","Denmark","Djibouti","Dominica","Dominican Republic",
  "Ecuador","Egypt","El Salvador","Equatorial Guinea","Eritrea","Estonia","Eswatini","Ethiopia","Fiji","Finland",
  "France","Gabon","Gambia","Georgia","Germany","Ghana","Greece","Grenada","Guatemala","Guinea",
  "Guinea-Bissau","Guyana","Haiti","Honduras","Hungary","Iceland","India","Indonesia","Iran","Iraq",
  "Ireland","Israel","Italy","Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kiribati","Kosovo",
  "Kuwait","Kyrgyzstan","Laos","Latvia","Lebanon","Lesotho","Liberia","Libya","Liechtenstein","Lithuania",
  "Luxembourg","Madagascar","Malawi","Malaysia","Maldives","Mali","Malta","Marshall Islands","Mauritania","Mauritius",
  "Mexico","Micronesia","Moldova","Monaco","Mongolia","Montenegro","Morocco","Mozambique","Myanmar","Namibia",
  "Nauru","Nepal","Netherlands","New Zealand","Nicaragua","Niger","Nigeria","North Korea","North Macedonia","Norway",
  "Oman","Pakistan","Palau","Palestine","Panama","Papua New Guinea","Paraguay","Peru","Philippines","Poland",
  "Portugal","Qatar","Romania","Russia","Rwanda","Saint Kitts and Nevis","Saint Lucia","Saint Vincent and the Grenadines",
  "Samoa","San Marino","Sao Tome and Principe","Saudi Arabia","Senegal","Serbia","Seychelles","Sierra Leone","Singapore",
  "Slovakia","Slovenia","Solomon Islands","Somalia","South Africa","South Korea","South Sudan","Spain","Sri Lanka","Sudan",
  "Suriname","Sweden","Switzerland","Syria","Taiwan","Tajikistan","Tanzania","Thailand","Timor-Leste","Togo",
  "Tonga","Trinidad and Tobago","Tunisia","Turkey","Turkmenistan","Tuvalu","Uganda","Ukraine","United Arab Emirates",
  "United Kingdom","United States","Uruguay","Uzbekistan","Vanuatu","Vatican City","Venezuela","Vietnam","Yemen","Zambia","Zimbabwe"
];

const SECTORS = [
  "Waste Management & Environmental Services", "Renewable Energy & Clean Tech", "Healthcare & Hospitals", "Pharmaceuticals",
  "Financial Services & FinTech", "Agriculture & Agribusiness", "Mining & Extractive Industries",
  "Telecommunications & ICT", "Water & Sanitation", "Education & EdTech", "Construction & Real Estate",
  "Transport & Logistics", "Oil & Gas", "Manufacturing & Industrial", "Retail & E-Commerce",
  "Tourism & Hospitality", "Food Processing & Beverages", "Textiles & Apparel", "Automotive & Mobility",
  "Aerospace & Defense", "Chemicals & Petrochemicals", "Cement & Building Materials", "Steel & Metals",
  "Forestry & Paper", "Fisheries & Aquaculture", "Power Generation & Utilities", "Smart Cities & Urban Tech",
  "InsurTech & Insurance", "Cybersecurity & Data Privacy", "Artificial Intelligence & Robotics",
  "Blockchain & Digital Assets", "Media & Entertainment", "Sports & Recreation", "Legal & Professional Services",
  "Consulting & Advisory", "Shipping & Maritime", "Aviation & Airports", "Rail & Public Transit",
  "Ports & Free Zones", "Warehousing & Cold Chain", "Plastic Recycling & Circular Economy",
  "Carbon Credits & Climate Finance", "Microfinance & Impact Investing", "Social Enterprise & NGO Services",
  "Beauty & Personal Care", "Consumer Electronics", "Packaging & Printing", "Security & Surveillance",
  "Biotechnology & Life Sciences", "Space Technology & Satellite", "Nuclear Energy",
  "Electric Vehicles & Battery Tech", "Hydrogen & Green Fuels", "Digital Health & Telemedicine",
  "Precision Agriculture & AgriTech", "Rare Earth & Critical Minerals", "Semiconductor & Chip Manufacturing",
  "Quantum Computing & Advanced R&D", "Drone & UAV Technology", "3D Printing & Additive Manufacturing",
  "Nanotechnology & Advanced Materials", "PropTech & Real Estate Tech", "RegTech & Compliance",
  "HealthTech & Wearables", "FoodTech & Alternative Protein", "Climate Adaptation & Resilience",
  "Waste-to-Energy & Biomass", "Desalination & Water Tech", "GovTech & Digital Government",
  "LegalTech & Justice Innovation", "Gaming & Interactive Entertainment",
  "Music & Creative Industries", "Film & Animation Studios",
  "Pet Care & Animal Health", "Senior Care & Aging Services",
  "Mental Health & Wellness", "Luxury Goods & Premium Brands",
  "Event Management & MICE", "Heritage Tourism & Cultural Assets", "Eco-Tourism & Sustainable Travel",
  "Sports Tech & Analytics", "eSports & Competitive Gaming",
  "Vertical Farming & Indoor Agriculture", "Aquaponics & Hydroponics",
  "Forest Carbon & REDD+ Projects", "Ocean Economy & Blue Finance", "Geothermal Energy",
  "Tidal & Wave Energy", "Small Modular Reactors (SMR)", "Green Building & LEED Certification",
  "Industrial IoT & Industry 4.0", "Supply Chain & Trade Finance", "Cross-Border Payments & Remittances",
  "Islamic Finance & Sukuk", "Sovereign Wealth Fund Advisory", "Infrastructure Bonds & PPP Finance",
  "Data Centers & Cloud Infrastructure", "Fiber Optics & Broadband", "5G Networks & Spectrum",
  "EduTech & Workforce Development", "Co-Working & Flexible Spaces",
  "Emission Monitoring & MRV", "CEMS (Continuous Emissions Monitoring)", "Carbon Trading & Carbon Markets",
  "Carbon Capture & Storage (CCS)", "Carbon Offset & Voluntary Markets", "Methane Detection & Abatement",
  "Air Quality Monitoring & Sensors", "ESG Reporting & Disclosure Tech", "Climate Risk Analytics",
  "Sustainable Aviation Fuels (SAF)", "Green Hydrogen Production", "Battery Recycling & Second Life",
  "Circular Economy & Material Recovery", "Environmental Remediation", "Biodiversity Credits & Nature Finance"
];

import { evaluateRisk } from '../utils/riskEvaluation';

// Section key → tier mapping (mirrors BlueprintViewPage SECTIONS config)
const TIER_KEYS = {
  scout: ['country_snapshot', 'executive_summary', 'market_opportunity', 'geopolitics_foreign_relations',
          'political_governance', 'legal_regulatory_framework', 'economic_data_deep_dive',
          'energy_infrastructure', 'trade_logistics_hubs', 'labor_social_indicators',
          'cultural_context', 'historical_context'],
  strategist: ['risk_analysis', 'financial_projections', 'industry_deep_dive', 'value_chain_mapping',
               'ma_deal_landscape', 'fdi_incentives_ez', 'tax_structuring', 'customs_trade_flows',
               'payments_banking', 'hr_talent_cost'],
  sovereign: ['sovereign_risk', 'competitive_positioning', 'implementation_roadmap',
              'sector_outlook', 'risk_mitigation_playbook', 'regulatory_horizon', 'climate_esg_risk',
              'stakeholder_map_scenarios'],
};
const ALL_TIER_KEYS = [...TIER_KEYS.scout, ...TIER_KEYS.strategist, ...TIER_KEYS.sovereign];
const TIER_META = [
  { key: 'scout',      label: 'Scout',      from: '#60a5fa', to: '#3b82f6' },
  { key: 'strategist', label: 'Strategist', from: '#6ee7b7', to: '#10b981' },
  { key: 'sovereign',  label: 'Sovereign',  from: '#c4b5fd', to: '#ec4899' },
];

function MiniJourneyBar({ sectionsGenerated, incomplete = 0 }) {
  // Overall progress
  const totalDone = sectionsGenerated.filter(k => ALL_TIER_KEYS.includes(k)).length;
  const totalPct = (totalDone / ALL_TIER_KEYS.length) * 100;

  return (
    <div className="space-y-1.5" data-testid="mini-journey-bar">
      {/* Thin 3-segment tier bar */}
      <div className="flex items-center gap-1.5">
        {TIER_META.map(t => {
          const done = TIER_KEYS[t.key].filter(k => sectionsGenerated.includes(k)).length;
          const total = TIER_KEYS[t.key].length;
          const pct = total ? (done / total) * 100 : 0;
          return (
            <div key={t.key} className="flex-1 relative h-1 rounded-full bg-[#eff4ff] overflow-hidden" title={`${t.label}: ${done}/${total}`}>
              <div
                className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-700 ease-out"
                style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${t.from}, ${t.to})` }}
              />
            </div>
          );
        })}
      </div>
      {/* Legend + percent + completeness pill */}
      <div className="flex items-center justify-between text-[9px] gap-1">
        <div className="flex items-center gap-2 text-[#737686]">
          {TIER_META.map(t => {
            const done = TIER_KEYS[t.key].filter(k => sectionsGenerated.includes(k)).length;
            return (
              <span key={t.key} className="inline-flex items-center gap-0.5">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: `linear-gradient(90deg, ${t.from}, ${t.to})` }} />
                {done}/{TIER_KEYS[t.key].length}
              </span>
            );
          })}
        </div>
        <div className="flex items-center gap-1.5">
          {incomplete > 0 && (
            <span
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 font-bold tracking-wide"
              title={`${incomplete} section${incomplete > 1 ? 's are' : ' is'} missing the End-of-Section marker (truncated)`}
              data-testid="incomplete-pill"
            >
              <svg width="7" height="7" viewBox="0 0 10 10" className="shrink-0"><circle cx="5" cy="5" r="4" fill="#b45309" /></svg>
              {incomplete} incomplete
            </span>
          )}
          <span className="font-mono font-bold text-[#0b1c30]">{Math.round(totalPct)}%</span>
        </div>
      </div>
    </div>
  );
}

function CountriesPage() {
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', sector: 'Waste Management & Environmental Services' });
  const [countrySearch, setCountrySearch] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [quota, setQuota] = useState(null);
  const [completeness, setCompleteness] = useState({});
  const [sweeping, setSweeping] = useState(false);
  // Regional blocs (EAC, ECOWAS, EU, ASEAN, …) — shown grouped at the top of
  // the country picker so users can generate holistic multi-country reports.
  const [blocs, setBlocs] = useState({ continents: [], groups: {} });

  useEffect(() => { fetchCountries(); fetchQuota(); fetchCompleteness(); fetchBlocs(); }, []);

  const fetchBlocs = async () => {
    try {
      const r = await axios.get(`${API}/regional-blocs`);
      setBlocs(r.data || { continents: [], groups: {} });
    } catch { /* non-fatal */ }
  };

  const fetchQuota = async () => {
    try { const res = await axios.get(`${API}/user/report-quota`); setQuota(res.data); } catch {}
  };

  const fetchCompleteness = async () => {
    try { const res = await axios.get(`${API}/countries/completeness-summary`); setCompleteness(res.data || {}); } catch {}
  };

  // Fleet-wide totals derived from the completeness map
  const fleetTotals = React.useMemo(() => {
    const ids = Object.keys(completeness);
    let generated = 0, complete = 0, incomplete = 0;
    ids.forEach(id => {
      const c = completeness[id];
      if (!c) return;
      generated += c.total_generated || 0;
      complete += c.complete || 0;
      incomplete += c.incomplete || 0;
    });
    return { countries: ids.length, generated, complete, incomplete };
  }, [completeness]);

  const handleRegenerateFleetIncomplete = async () => {
    if (fleetTotals.incomplete === 0) { toast.info('Entire fleet is healthy — no incomplete sections.'); return; }
    const eta = Math.ceil(fleetTotals.incomplete * 1.3);
    if (!window.confirm(`Regenerate ${fleetTotals.incomplete} incomplete section${fleetTotals.incomplete > 1 ? 's' : ''} across ${Object.values(completeness).filter(c => c.incomplete > 0).length} countries?\n\nThis will run in the background (~${eta} min). You can keep using the app.`)) return;
    setSweeping(true);
    try {
      const r = await axios.post(`${API}/countries/regenerate-incomplete-fleet`);
      toast.success(`Fleet sweep queued — ${r.data.queued_sections} section${r.data.queued_sections > 1 ? 's' : ''} across ${r.data.queued_countries} country/ies. ${r.data.detail.split('.').slice(1).join('.').trim() || ''}`, { duration: 6000 });
      // Poll completeness every ~25s while the sweep runs
      const poll = setInterval(fetchCompleteness, 25000);
      setTimeout(() => clearInterval(poll), 1000 * 60 * Math.min(15, eta + 2));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fleet sweep failed');
    } finally {
      setSweeping(false);
    }
  };

  const fetchCountries = async () => {
    try {
      
      const response = await axios.get(`${API}/countries`, {  });
      setCountries(response.data);
    } catch (error) {
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        toast.error('Session expired — please log in again');
        localStorage.removeItem('token');
        setTimeout(() => { window.location.href = '/login'; }, 1200);
      } else {
        toast.error(error.response?.data?.detail || `Failed to fetch countries${status ? ` (HTTP ${status})` : ''}`);
      }
    }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // formData.name may be a bloc reference of the form "bloc:eac" — split it
      // before submitting so the backend sees a clean payload.
      let payload = { ...formData };
      if (typeof payload.name === 'string' && payload.name.startsWith('bloc:')) {
        payload.bloc_code = payload.name.slice(5);
        payload.name = payload.bloc_code;  // backend will rename to the bloc's display label
      }
      await axios.post(`${API}/countries`, payload, {  });
      toast.success(payload.bloc_code ? 'Regional bloc added' : 'Country added');
      setIsOpen(false);
      setFormData({ name: '', sector: 'Waste Management & Environmental Services' });
      setCountrySearch('');
      fetchCountries();
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.detail?.includes('limit')) {
        toast.error('Report limit reached. Purchase more reports to continue.', {
          action: { label: 'View Plans', onClick: () => navigate('/pricing') },
          duration: 8000
        });
        setIsOpen(false);
      } else {
        toast.error(error.response?.data?.detail || 'Failed');
      }
    }
  };

  const handleDelete = async (e, countryId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this country?')) return;
    try {
      
      await axios.delete(`${API}/countries/${countryId}`, {  });
      toast.success('Deleted');
      fetchCountries();
    } catch (error) { toast.error('Failed'); }
  };

  const handleDownloadPDF = async (e, countryId, countryName) => {
    e.stopPropagation();
    try {
      
      toast.loading('Generating PDF — this may take up to 60 seconds…', { id: 'pdf-gen' });
      const response = await axios.get(`${API}/countries/${countryId}/blueprint/pdf`, {
        responseType: 'blob',
        timeout: 300000,  // 5 min — PDF rendering can take 30-60s for full blueprints
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SA_${countryName}_Blueprint_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.dismiss('pdf-gen');
      toast.success('PDF downloaded!');
    } catch (error) {
      toast.dismiss('pdf-gen');
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        toast.error('Session expired — please log in again');
      } else if (error.code === 'ECONNABORTED' || (error.message || '').includes('timeout')) {
        toast.error('PDF generation timed out. The AI service may be busy — please try again.');
      } else {
        toast.error(error.response?.data?.detail || `PDF error${status ? ` (HTTP ${status})` : ''} — please retry`);
      }
    }
  };

  const filtered = countries.filter(c => 
    c.name.toLowerCase().includes(filterSearch.toLowerCase()) ||
    (c.sector || '').toLowerCase().includes(filterSearch.toLowerCase()) ||
    (c.region || '').toLowerCase().includes(filterSearch.toLowerCase())
  );

  const getRisk = (country) => evaluateRisk(country);

  return (
    <div data-testid="countries-page" className="min-h-screen bg-[#f8f9ff] text-[#0b1c30]" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Dynamic kinetic hero */}
      <style>{`
        @keyframes slowSpinCP { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .animate-slowSpinCP { animation: slowSpinCP 40s linear infinite; }
        .grid-bg-cp { background-image: linear-gradient(rgba(0,74,198,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,74,198,0.05) 1px, transparent 1px); background-size: 40px 40px; }
      `}</style>
      <section className="relative overflow-hidden bg-gradient-to-br from-[#f8f9ff] via-white to-[#eff4ff] border-b border-[#c3c6d7]/20">
        <div className="absolute inset-0 grid-bg-cp opacity-60 pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-gradient-to-br from-[#004ac6]/5 via-[#7c3aed]/5 to-transparent animate-slowSpinCP pointer-events-none" />
        <div className="relative px-6 lg:px-8 py-8 lg:py-10 max-w-[1400px] mx-auto">
          <div className="inline-flex items-center gap-2 bg-white border border-[#c3c6d7]/40 rounded-full px-3 py-1 mb-4 shadow-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#434655]">Multi-tenant · isolated per user</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter text-[#0b1c30] leading-[1.05] mb-2">
            Intelligence <span className="bg-gradient-to-r from-[#004ac6] to-[#7c3aed] bg-clip-text text-transparent">Targets</span>
          </h1>
          <p className="text-sm text-[#434655] max-w-2xl">
            All monitored territories with sovereign risk assessment and blueprint status — track up to 180+ countries across 115 sectors.
          </p>
        </div>
      </section>

      {/* Fleet Health strip */}
      {Object.keys(completeness).length > 0 && (
        <div className={`border-b ${fleetTotals.incomplete > 0 ? 'bg-gradient-to-r from-amber-50 via-white to-white border-amber-200/50' : 'bg-gradient-to-r from-emerald-50 via-white to-white border-emerald-200/50'}`} data-testid="fleet-health-strip">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-3 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-[11px]">
              <span className={`w-2 h-2 rounded-full ${fleetTotals.incomplete > 0 ? 'bg-amber-500 animate-pulse' : 'bg-emerald-500'}`} />
              <span className="font-bold uppercase tracking-[0.15em] text-[10px] text-[#434655]">Fleet Health</span>
            </div>
            <div className="flex items-center gap-5 text-[11px] flex-1 flex-wrap">
              <div className="flex items-center gap-1.5">
                <span className="text-[#737686]">Countries</span>
                <span className="font-extrabold text-[#0b1c30]">{fleetTotals.countries}</span>
              </div>
              <div className="w-px h-4 bg-[#c3c6d7]/40" />
              <div className="flex items-center gap-1.5">
                <span className="text-[#737686]">Sections generated</span>
                <span className="font-extrabold text-[#0b1c30]">{fleetTotals.generated}</span>
              </div>
              <div className="w-px h-4 bg-[#c3c6d7]/40" />
              <div className="flex items-center gap-1.5">
                <span className="text-[#737686]">Complete</span>
                <span className="font-extrabold text-emerald-600">{fleetTotals.complete}</span>
              </div>
              <div className="w-px h-4 bg-[#c3c6d7]/40" />
              <div className="flex items-center gap-1.5">
                <span className="text-[#737686]">Incomplete</span>
                <span className={`font-extrabold ${fleetTotals.incomplete > 0 ? 'text-amber-700' : 'text-[#0b1c30]'}`}>{fleetTotals.incomplete}</span>
              </div>
            </div>
            {fleetTotals.incomplete > 0 ? (
              <button
                onClick={handleRegenerateFleetIncomplete}
                disabled={sweeping}
                data-testid="regen-fleet-btn"
                className={`flex items-center gap-2 text-[11px] font-semibold rounded-lg px-3.5 py-1.5 transition-all border ${sweeping ? 'bg-[#f8f9ff] border-[#c3c6d7]/40 text-[#737686] cursor-not-allowed' : 'bg-[#0b1c30] hover:bg-[#1a2d4a] border-[#0b1c30] text-white shadow-sm'}`}
              >
                <RefreshCw className={`w-3.5 h-3.5 ${sweeping ? 'animate-spin' : ''}`} />
                {sweeping ? 'Queueing…' : `Regenerate All Incomplete (${fleetTotals.incomplete})`}
              </button>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700" data-testid="fleet-health-all-green">
                <CheckCircle className="w-3.5 h-3.5" /> All sections complete
              </span>
            )}
          </div>
        </div>
      )}

      <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div />
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 text-[#434655] absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="text" placeholder="Filter territories..." value={filterSearch}
              onChange={e => setFilterSearch(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm bg-white border border-[#c3c6d7]/40 rounded-lg outline-none focus:border-[#004ac6] w-56"
              data-testid="filter-input" />
          </div>
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-country-btn" className="bg-[#004ac6] hover:bg-[#003ea8] text-white gap-2 text-xs font-bold">
                <Plus className="w-3.5 h-3.5" /> Add Territory
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Intelligence Target</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Country or Regional Bloc *</Label>
                  <Select value={formData.name} onValueChange={(value) => setFormData({ ...formData, name: value })}>
                    <SelectTrigger data-testid="country-name-select" className="w-full">
                      <SelectValue placeholder="Select a country or region" />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      <div className="px-2 py-1.5 sticky top-0 bg-white z-10">
                        <div className="flex items-center gap-2 border rounded-md px-2 py-1">
                          <Search className="w-4 h-4 text-[#c3c6d7]" />
                          <input type="text" placeholder="Search countries or regions..." className="outline-none text-sm w-full bg-transparent"
                            value={countrySearch} onChange={(e) => setCountrySearch(e.target.value)} data-testid="country-search-input" />
                        </div>
                      </div>
                      {/* ── Regional Blocs (EAC, ECOWAS, EU, ASEAN, …) ── */}
                      {(blocs.continents || []).flatMap((cont) => {
                        const list = (blocs.groups[cont] || []).filter(b =>
                          !countrySearch ||
                          b.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
                          (b.short || '').toLowerCase().includes(countrySearch.toLowerCase())
                        );
                        if (list.length === 0) return [];
                        return [
                          <div key={`hdr-${cont}`} className="px-2 pt-2 pb-1 text-[10px] font-black uppercase tracking-widest text-[#7c3aed] bg-[#f5f3ff] sticky-header" data-testid={`bloc-group-${cont}`}>
                            <Globe className="inline w-3 h-3 mr-1" />
                            {cont} · Regional Blocs
                          </div>,
                          ...list.map((b) => (
                            <SelectItem key={`bloc:${b.code}`} value={`bloc:${b.code}`} data-testid={`bloc-option-${b.code}`}>
                              <span className="font-bold">{b.name}</span>
                              <span className="text-[10px] text-[#737686] ml-2">· {b.member_count} members</span>
                            </SelectItem>
                          )),
                        ];
                      })}
                      {/* ── Individual Countries ── */}
                      <div className="px-2 pt-3 pb-1 text-[10px] font-black uppercase tracking-widest text-[#004ac6] bg-[#eff4ff]">
                        Individual Countries
                      </div>
                      {WORLD_COUNTRIES.filter(c => c.toLowerCase().includes(countrySearch.toLowerCase())).map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.name && formData.name.startsWith && formData.name.startsWith('bloc:') && (() => {
                    const code = formData.name.slice(5);
                    let bloc = null;
                    for (const cont of (blocs.continents || [])) {
                      const found = (blocs.groups[cont] || []).find(b => b.code === code);
                      if (found) { bloc = found; break; }
                    }
                    if (!bloc) return null;
                    return (
                      <div className="text-[11px] text-[#434655] bg-[#f5f3ff] border border-[#e9d5ff] rounded-md p-2 mt-1" data-testid="bloc-preview">
                        <div className="font-bold text-[#7c3aed] mb-1">{bloc.short}</div>
                        <div className="leading-relaxed">{bloc.members.slice(0, 12).join(', ')}{bloc.members.length > 12 ? `, +${bloc.members.length - 12} more` : ''}</div>
                      </div>
                    );
                  })()}
                </div>
                <div className="space-y-2">
                  <Label>Sector *</Label>
                  <Select value={formData.sector} onValueChange={(value) => setFormData({ ...formData, sector: value })}>
                    <SelectTrigger data-testid="sector-select" className="w-full">
                      <SelectValue placeholder="Select sector" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {SECTORS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" data-testid="submit-country-btn" className="w-full bg-[#004ac6] hover:bg-[#003ea8] text-white" disabled={!formData.name}>
                  {formData.name && formData.name.startsWith && formData.name.startsWith('bloc:') ? 'Add Regional Bloc' : 'Add Territory'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Report Quota Banner */}
      {quota && !quota.is_admin && (
        <div className="mb-6 bg-white rounded-xl shadow-sm p-4 flex items-center justify-between" data-testid="report-quota-banner">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-[#004ac6]" />
              <span className="text-xs text-[#434655]">Reports Used: <strong className="text-[#0b1c30]">{Math.max(0, quota.reports_used - 1)}</strong></span>
            </div>
            {quota.paid_reports > 0 && (
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-[#00A651]" />
                <span className="text-xs text-[#434655]">Paid: <strong className="text-[#00A651]">{quota.paid_reports}</strong></span>
              </div>
            )}
            {quota.free_from_admin > 0 && (
              <div className="flex items-center gap-2">
                <Gift className="w-4 h-4 text-[#004ac6]" />
                <span className="text-xs text-[#434655]">Gift: <strong className="text-[#004ac6]">{quota.free_from_admin}</strong></span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#434655]">Remaining: <strong className={quota.reports_remaining > 0 ? 'text-[#00A651]' : 'text-red-500'}>{quota.reports_remaining}</strong></span>
            </div>
          </div>
          {quota.reports_remaining === 0 && (
            <button onClick={() => navigate('/pricing')} data-testid="upgrade-btn"
              className="bg-[#004ac6] text-white text-xs font-bold px-4 py-2 rounded-lg hover:bg-[#003ea8] transition-all flex items-center gap-1.5">
              <CreditCard className="w-3.5 h-3.5" /> Buy More Reports
            </button>
          )}
        </div>
      )}

      {/* Stats Bar */}
      <div className="flex items-center gap-6 mb-6 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#3b82f6]" />
          <span className="text-[#434655]">Stable: {countries.filter(c => evaluateRisk(c).level === 'STABLE').length}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#f59e0b]" />
          <span className="text-[#434655]">Caution: {countries.filter(c => ['CAUTION', 'MODERATE'].includes(evaluateRisk(c).level)).length}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#ef4444]" />
          <span className="text-[#434655]">High Risk: {countries.filter(c => evaluateRisk(c).level === 'HIGH RISK').length}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-gray-400" />
          <span className="text-[#434655]">Unassessed: {countries.filter(c => evaluateRisk(c).level === 'UNASSESSED').length}</span>
        </div>
        <span className="text-[#c3c6d7]">|</span>
        <span className="text-[#434655]">{countries.length} territories total</span>
      </div>

      {/* Country Grid — Key Intelligence Targets Style */}
      {loading ? (
        <div className="text-center py-12 text-[#434655]">Loading intelligence targets...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-16 text-center">
          <Globe className="w-16 h-16 text-[#c3c6d7] mx-auto mb-4" />
          <p className="text-[#434655]">{countries.length === 0 ? 'No territories added yet.' : 'No results matching filter.'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.map((country) => {
            const progress = country.sections_generated?.length || 0;
            const risk = getRisk(country);
            const gdpGrowth = country.metrics_gdp_growth;
            const sovereignScore = country.metrics_sovereign_score;
            const hdi = country.metrics_hdi;
            const creditRating = country.metrics_credit_rating;

            return (
              <div key={country.id} onClick={() => navigate(`/platform/countries/${country.id}/blueprint`)}
                className="bg-white rounded-xl shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-all group"
                data-testid={`country-card-${country.id}`}>
                {/* Card Header with gradient */}
                <div className="h-32 bg-gradient-to-br from-[#0b1c30] via-[#1a365d] to-[#004ac6] relative overflow-hidden">
                  <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 70% 30%, #2563eb 0%, transparent 60%)' }} />
                  <div className="absolute bottom-3 left-4 right-4">
                    <h3 className="text-lg font-bold text-white tracking-tight group-hover:text-[#b4c5ff] transition-colors">{country.name}</h3>
                    <p className="text-[10px] text-white/50 uppercase tracking-widest">{country.region || 'Unclassified'}</p>
                  </div>
                  <span className={`absolute top-3 right-3 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                    risk.color === 'green' ? 'bg-green-500/20 text-green-300' :
                    risk.color === 'yellow' ? 'bg-yellow-500/20 text-yellow-300' :
                    'bg-white/10 text-white/60'
                  }`}>{risk.level}</span>
                  {/* Delete button */}
                  <button onClick={(e) => handleDelete(e, country.id)}
                    className="absolute top-3 left-3 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded bg-red-500/20 hover:bg-red-500/40"
                    data-testid={`delete-${country.id}`}>
                    <Trash2 className="w-3 h-3 text-red-300" />
                  </button>
                </div>

                {/* Card Body */}
                <div className="p-4">
                  <p className="text-[10px] text-[#004ac6] font-semibold truncate mb-3">{country.sector || 'General'}</p>

                  {/* Mini journey progress — per-tier fill bars + completeness pill */}
                  <div className="mb-3 p-2.5 rounded-lg bg-gradient-to-br from-[#f8f9ff] to-transparent border border-[#c3c6d7]/20">
                    <MiniJourneyBar sectionsGenerated={country.sections_generated || []} incomplete={completeness?.[country.id]?.incomplete || 0} />
                  </div>

                  <div className="flex justify-between text-xs mb-3">
                    <div>
                      <span className="text-[#434655]">GDP Growth</span>
                      <p className="font-bold text-[#0b1c30]">{gdpGrowth != null ? `+${gdpGrowth}%` : '—'}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-[#434655]">{creditRating ? 'Rating' : 'HDI'}</span>
                      <p className="font-bold text-[#0b1c30]">{creditRating ? creditRating.split(';')[0].trim() : (hdi != null ? hdi.toFixed(3) : '—')}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] text-[#434655]">Sovereign Score</span>
                    <span className="text-xs font-bold text-[#004ac6]">{sovereignScore != null ? `${sovereignScore}/10` : '—'}</span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-[#c3c6d7]/20">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#434655]">{progress}/31 sections</span>
                      {(country.pdf_versions?.length > 0 || country.latest_pdf_version) && (
                        <span className="flex items-center gap-0.5 text-[9px] font-semibold text-red-500 bg-red-50 px-1.5 py-0.5 rounded-full" title={`PDF v${country.latest_pdf_version || country.pdf_versions?.length}`}>
                          <FileText className="w-2.5 h-2.5" />PDF
                        </span>
                      )}
                      {country.gamma_url && (
                        <span className="flex items-center gap-0.5 text-[9px] font-semibold text-purple-500 bg-purple-50 px-1.5 py-0.5 rounded-full" title="Presentation available">
                          <Presentation className="w-2.5 h-2.5" />Deck
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {progress > 0 && (
                        <button onClick={(e) => handleDownloadPDF(e, country.id, country.name)}
                          className="text-[#434655] hover:text-[#004ac6] transition-colors" title="Download PDF">
                          <Download className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <ArrowUpRight className="w-3.5 h-3.5 text-[#c3c6d7] group-hover:text-[#004ac6] transition-colors" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}

export default CountriesPage;
